<template>
    <div class="bill-discounts-list" :class="{ disabled: isSaving }">
        <template v-if="!bill.isAnyItemSelected">
            <h3 v-if="itemLevelDiscounts.length > 0">{{ translations.general.error }}</h3>
            <div class="error-message">{{ translations.pay.failedBillInvalidSelection }}</div>
        </template>
        <template v-else>
            <h4 v-if="itemLevelDiscounts.length > 0">{{ translations.billDiscountsList.itemLevelDiscounts }}</h4>
            <div
                v-for="discount in itemLevelDiscounts"
                class="discount-item"
                :class="{ removable: !discount.isProcessed }"
                :key="discount.discountId"
                @click="removeItemLevelDiscount(discount)"
            >
                <span>{{ translate(discount.discountName) }}</span>
                <span class="applied-amount">{{ toMoneyWithSign(discount.appliedAmount) }}</span>
                <span v-if="!discount.isProcessed" class="remove-btn"><i class="fas fa-trash"></i></span>
                <span v-if="discount.isProcessed"></span>
            </div>

            <h4 v-if="discounts.length > 0">{{ translations.billDiscountsList.orderOrBillLevelDiscounts }}</h4>
            <template v-for="discount in discounts" :key="discount.id">
                <div
                    class="discount-item"
                    :class="{ removable: !discount.isProcessed }"
                    @click="removeDiscount(discount)"
                >
                    <span>{{ translate(discount.discountName) }}</span>
                    <span class="applied-amount">{{ toMoneyWithSign(bill.getAppliedBillDiscount(discount.discountId!)) }}</span>
                    <span v-if="!discount.isProcessed" class="remove-btn"><i class="fas fa-trash"></i></span>
                    <span v-if="discount.isProcessed"></span>
                </div>
            </template>

            <h4 v-if="vouchers.length > 0">{{ translations.billDiscountsList.vouchers }}</h4>
            <template v-for="voucher in vouchers" :key="voucher.id">
                <div class="discount-item" :class="{ removable: !voucher.isProcessed }" @click="removeVoucher(voucher)">
                    <span>{{
                        voucher.voucherCode?.substring(0, 4) +
                        "..." +
                        voucher.voucherCode?.substring(voucher.voucherCode.length - 4)
                    }}</span>
                    <span class="applied-amount">{{ toMoneyWithSign(voucher.amount) }}</span>
                    <span v-if="!voucher.isProcessed" class="remove-btn"><i class="fas fa-trash"></i></span>
                    <span v-if="voucher.isProcessed"></span>
                </div>
            </template>
        </template>
    </div>
</template>

<script setup lang="ts">
    import { Popup } from "@/components/dialog/Popup";
    import { Factory } from "@/data/dao/Factory";
    import { CustomerDao } from "@/data/dao/service/CustomerDao";
    import type { IBill, IBillDiscount, IBillItemDiscountSummary } from "@/data/model/Bill";
    import { useBillStore } from "@/data/stores/bill";
    import { translate } from "@/shared/localization/language";
    import { translations } from "@/shared/localization/translations";
    import { logError } from "@/shared/logging/errors";
    import { toMoneyWithSign } from "@/shared/utils/format";
    import { computed, ref } from "vue";

    const customerDao = Factory.getInstance<CustomerDao>(CustomerDao);
    const bill = useBillStore();

    const emit = defineEmits(["discountRemoved", "allDiscountsRemoved"]);

    const isSaving = ref(false);
    const itemLevelDiscounts = computed(() => bill.currentBill?.appliedItemDiscountSummaries ?? []);
    const discounts = computed(() =>
        (bill.currentBill?.appliedDiscounts ?? [])
            .filter((d) => d.discountId != null)
            .sort((a, b) => a.createdDateTime.getTime() - b.createdDateTime.getTime())
    );
    const vouchers = computed(() =>
        (bill.currentBill?.appliedDiscounts ?? [])
            .filter((d) => d.voucherId != null)
            .sort((a, b) => a.createdDateTime.getTime() - b.createdDateTime.getTime())
    );
    const isAnyDiscountVisible = computed(
        () => itemLevelDiscounts.value.length > 0 || discounts.value.length > 0 || vouchers.value.length > 0
    );

    // define html refs
    async function removeItemLevelDiscount(discount: IBillItemDiscountSummary) {
        if (discount.isProcessed) {
            return;
        }
        removeSelectedDiscount(() => customerDao.removeBillItemDiscount(bill.currentBill!.id, discount.discountId));
    }

    async function removeDiscount(discount: IBillDiscount) {
        if (discount.isProcessed) {
            return;
        }
        removeSelectedDiscount(() => customerDao.removeBillDiscount(bill.currentBill!.id, discount.discountId!));
    }

    async function removeVoucher(voucher: IBillDiscount) {
        if (voucher.isProcessed) {
            return;
        }
        removeSelectedDiscount(() => customerDao.removeBillVoucher(bill.currentBill!.id, voucher.voucherId!), true);
    }

    async function removeSelectedDiscount(billRemoveAction: () => Promise<IBill>, isVoucher: boolean = false) {
        const message = isVoucher
            ? translations.discounts.removeVoucherPrompt
            : translations.discounts.removeDiscountPrompt;

        Popup.show(
            {
                message: message,
                confirmHandler: async () => {
                    isSaving.value = true;

                    try {
                        const currentBill = await billRemoveAction();
                        bill.setCurrentBill(currentBill);
                        emit("discountRemoved");

                        if (!isAnyDiscountVisible.value) {
                            emit("allDiscountsRemoved");
                        }
                    } catch (ex) {
                        logError(ex);
                        handleError();
                    } finally {
                        isSaving.value = false;
                    }
                },
                confirmButton: translations.general.remove,
                cancelButton: translations.general.cancel,
                highlightConfirm: false,
                highlightCancel: true,
                confirmBtnOnTheLeft: false,
                uniqueId: "confirmBillDiscountRemovalPopup",
                containerClass: "confirm-bill-discount-removal-popup",
            },
            $("body")
        );
    }

    function handleError() {
        Popup.show(
            {
                containerClass: "remove-bill-discount-error-popup",
                title: translations.general.error,
                message: translations.general.operationFailed,
            },
            $("body")
        );
    }
</script>

<style lang="scss">
    @use "./../../../assets/styles/scss/shared/_variables.scss";
    @use "./../../../assets/styles/scss/shared/_colors.scss";

    .bill-discounts-list {
        display: table;

        h4 {
            margin-top: 1em;
            margin-bottom: 0.5em;
            font-weight: 500;
            line-height: 1.2;

            &:first-child {
                margin-top: 0;
            }
        }

        .discount-item {
            display: table-row;
            transition: opacity 0.3s variables.$easeOutExpo;

            &.removable {
                cursor: pointer;

                &:hover {
                    opacity: 0.5;
                }
            }

            > span {
                display: table-cell;
                &:first-child {
                    width: 100%;
                }

                i {
                    padding: 7px;
                }
            }

            .remove-btn {
                color: #484848;
                padding-left: 10px;
            }

            &.disabled {
                cursor: default;
                opacity: 0.5;

                &.removable {
                    cursor: default;

                    &:hover {
                        opacity: 0.5;
                    }
                }
            }
        }

        .error-message {
            color: colors.$fontColor;
        }
    }

    .confirm-bill-discount-removal-popup {
        z-index: 1510;
    }

    .remove-bill-discount-error-popup {
        z-index: 1511;
    }
</style>
